import { ChartSpec } from "@Interfaces/Entities/AssistantCharts";

const STORAGE_KEY_PREFIX = "assistant-chart-";
const ASSISTANT_CHART_SOURCE = "assistant_chat";

export type StoredAssistantChart = {
    channelId: string;
    chartId: string;
    originalChartId?: string;
    createdAt: string;
    source: typeof ASSISTANT_CHART_SOURCE;
    chart: ChartSpec;
};

const isSafeChartId = (chartId: string): boolean =>
    /^[A-Za-z0-9_-]{1,128}$/.test(chartId);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

// One client id owns the localStorage entry, the DeepChat button target,
// and the ApexCharts instance id used later for image export.
const createClientChartId = (): string =>
    `assistant_chart_${crypto.randomUUID()}`;

const buildStorageKey = (chartId: string): string =>
    `${STORAGE_KEY_PREFIX}${chartId}`;

const getOriginalChartId = (chart: ChartSpec): string | undefined => {
    const originalChartId = chart.id?.trim();
    return originalChartId || undefined;
};

const getStorage = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const parseStoredAssistantChart = (
    raw: string,
    expectedChartId?: string
): StoredAssistantChart | null => {
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!isRecord(parsed)) return null;

        if (expectedChartId && parsed.chartId !== expectedChartId) return null;
        if (typeof parsed.chartId !== "string") return null;
        if (!isSafeChartId(parsed.chartId)) return null;
        if (typeof parsed.channelId !== "string") return null;
        if (typeof parsed.createdAt !== "string") return null;
        if (parsed.source !== ASSISTANT_CHART_SOURCE) return null;
        if (!isRecord(parsed.chart)) return null;

        return parsed as StoredAssistantChart;
    } catch {
        return null;
    }
};

export const saveAssistantChart = (
    channelId: string,
    chart: ChartSpec
): StoredAssistantChart => {
    const storage = getStorage();
    const chartId = createClientChartId();
    const createdAt = new Date().toISOString();
    const originalChartId = getOriginalChartId(chart);

    // Assistant-provided ids can repeat. Store them separately and render with
    // the unique client id so old chat buttons keep opening the right chart.
    const normalizedChart: ChartSpec = { ...chart, id: chartId };

    const stored: StoredAssistantChart = {
        channelId,
        chartId,
        ...(originalChartId ? { originalChartId } : {}),
        createdAt,
        source: ASSISTANT_CHART_SOURCE,
        chart: normalizedChart,
    };

    if (!storage) return stored;

    try {
        storage.setItem(buildStorageKey(chartId), JSON.stringify(stored));
    } catch {
        // ignore persistence errors
    }

    return stored;
};

export const getAssistantChart = (
    chartId: string
): StoredAssistantChart | null => {
    const storage = getStorage();
    if (!storage) return null;
    if (!isSafeChartId(chartId)) return null;

    try {
        const raw = storage.getItem(buildStorageKey(chartId));
        if (!raw) return null;

        return parseStoredAssistantChart(raw, chartId);
    } catch {
        return null;
    }
};

export const removeAssistantChart = (chartId: string): void => {
    const storage = getStorage();
    if (!storage) return;

    if (!isSafeChartId(chartId)) return;

    try {
        storage.removeItem(buildStorageKey(chartId));
    } catch {
        // ignore removal errors
    }
};

export const listAssistantChartsForChannel = (
    channelId: string
): StoredAssistantChart[] => {
    const storage = getStorage();
    if (!storage) return [];

    const results: StoredAssistantChart[] = [];
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (!key || !key.startsWith(STORAGE_KEY_PREFIX)) continue;

            const raw = storage.getItem(key);
            if (!raw) continue;

            const stored = parseStoredAssistantChart(raw);
            if (stored?.channelId === channelId) results.push(stored);
        }
    } catch {
        return [];
    }

    return results.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
};
